import { lazy, Suspense } from "react"
import { lazyWithRetry } from "@edemics/lib/lazy-with-retry"
import { Routes, Route, Navigate, useLocation } from "react-router-dom"
import { ErrorBoundary } from "react-error-boundary"
import { Providers } from "./app/Providers"
import { AppShell } from "./app/AppShell"
import { ErrorFallback } from "./app/ErrorFallback"
import { RouteLoadingFallback } from "./app/LoadingFallback"
import { ModuleGate } from "@edemics/components/module-gate"
import { AuthGuard } from "./app/AuthGuard"
import { TenantGuard } from "./app/TenantGuard"

/**
 * Every module route is lazy-loaded via React.lazy so first-paint only
 * downloads the shell + the module the user actually landed on. Vite
 * emits one chunk per lazy() boundary — the dashboard is ~5 KB gz,
 * accounting will land around 100-200 KB gz, etc.
 *
 * NEW MODULES: add `const XxxPages = lazyWithRetry(() => import(...))` below and a
 * matching <Route path="/xxx/*" element={<XxxPages />}> inside AppShell.
 */
const DashboardPage = lazyWithRetry(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
)

// Wave S (2026-09-10) — tenant Settings → Custom domain (BYO hostname).
const SettingsDomainsPage = lazyWithRetry(() =>
  import("./pages/settings/DomainsPage").then((m) => ({ default: m.default })),
)

// Federation Phase 1 (2026-09-10) — publisher-tenant course authoring.
// Reachable only when tenantKind ∈ {publisher, hybrid}; BE returns 403
// for schools, and the sidebar gate hides the entry via
// SAModule.disabledForKinds.
const CoursesListPage = lazyWithRetry(() =>
  import("./pages/courses/CoursesListPage").then((m) => ({ default: m.default })),
)
const CourseEditPage = lazyWithRetry(() =>
  import("./pages/courses/CourseEditPage").then((m) => ({ default: m.default })),
)

// Federation Phase 2 (2026-09-10) — entitlement plane.
const PublisherListingsPage = lazyWithRetry(() =>
  import("./pages/publisher/ListingsPage").then((m) => ({ default: m.default })),
)
const CataloguePage = lazyWithRetry(() =>
  import("./pages/catalogue/CataloguePage").then((m) => ({ default: m.default })),
)
const AdoptionsListPage = lazyWithRetry(() =>
  import("./pages/adoptions/AdoptionsListPage").then((m) => ({ default: m.default })),
)
const AdoptionEditPage = lazyWithRetry(() =>
  import("./pages/adoptions/AdoptionEditPage").then((m) => ({ default: m.default })),
)

// Federation Phase 4 (2026-09-10) — statements.
const PublisherStatementPage = lazyWithRetry(() =>
  import("./pages/publisher/StatementPage").then((m) => ({ default: m.default })),
)
const ConsumerStatementPage = lazyWithRetry(() =>
  import("./pages/statements/ConsumerStatementPage").then((m) => ({ default: m.default })),
)
const PublisherAnalyticsPage = lazyWithRetry(() =>
  import("./pages/publisher/AnalyticsPage").then((m) => ({ default: m.default })),
)
// Federation Phase 4 follow-up — public guest preview surface.
// Route lives OUTSIDE the AppShell (no auth). Tenant resolves via
// Host / X-Tenant-Host on the BE call.
const GuestPreviewPage = lazyWithRetry(() =>
  import("./pages/guest/GuestPreviewPage").then((m) => ({ default: m.default })),
)

// Auth surface — public routes outside AppShell. `each app owns its auth`
// decision: tenant-app has its own login/register/otp so 401 redirects have
// a target that stays inside this app (no cross-domain hop to landing).
const LoginPage = lazyWithRetry(() =>
  import("./pages/auth/LoginPage").then((m) => ({ default: m.default })),
)
const RegisterPage = lazyWithRetry(() =>
  import("./pages/auth/RegisterPage").then((m) => ({ default: m.default })),
)
const OtpVerificationPage = lazyWithRetry(() =>
  import("./pages/auth/OtpVerificationPage").then((m) => ({ default: m.default })),
)
// Force-password-change surface. Authenticated route (needs the JWT so
// /auth/change-password can identify the user), but sits OUTSIDE the
// AppShell chrome — the AuthGuard's own redirect brings the user here
// with no sidebar/header when their session has mustChangePassword=true.
const ChangePasswordPage = lazyWithRetry(() =>
  import("./pages/auth/ChangePasswordPage").then((m) => ({ default: m.default })),
)
// Self-service password reset (2026-09-11) — both routes are public,
// outside AppShell. /forgot-password collects an email and POSTs
// /auth/forgot-password; /reset-password consumes the one-shot token
// from ?token= and POSTs /auth/reset-password.
const ForgotPasswordPage = lazyWithRetry(() =>
  import("./pages/auth/ForgotPasswordPage").then((m) => ({ default: m.default })),
)
const ResetPasswordPage = lazyWithRetry(() =>
  import("./pages/auth/ResetPasswordPage").then((m) => ({ default: m.default })),
)

// Public guest / admission surface — no auth, no sidebar, no ModuleGate.
// A prospective parent lands on /admission/apply from the school's public
// admission link, submits the multi-step form, then logs in with their
// generated access code on /admission/status or /guest-access to check status
// and pay fees. These are the only routes a non-authenticated user is
// expected to hit inside tenant-app; they mirror the same slugs in
// EdemicsAllModule (`app/admission/*`, `app/guest-access/*`).
//
// IMPORTANT: url path is `/admission/*` (singular) — distinct from the
// authenticated `/admissions/*` (plural) module surface.
const AdmissionApplyPage = lazyWithRetry(() =>
  import("../../landing/app/admission/apply/page").then((m) => ({ default: m.default })),
)
const AdmissionPayFeesPage = lazyWithRetry(() =>
  import("../../landing/app/admission/pay-fees/page").then((m) => ({ default: m.default })),
)
const AdmissionStatusPage = lazyWithRetry(() =>
  import("../../landing/app/admission/status/page").then((m) => ({ default: m.default })),
)
const GuestAccessPage = lazyWithRetry(() =>
  import("./features/guest-access/pages/page").then((m) => ({ default: m.default })),
)

// Accounting module — pilot pattern for every module migration in Phase 2+.
const AccountingRoutes = lazyWithRetry(() =>
  import("./features/accounting/routes").then((m) => ({ default: m.AccountingRoutes })),
)

// Finance module — same recipe as accounting.
const FinanceRoutes = lazyWithRetry(() =>
  import("./features/finance/routes").then((m) => ({ default: m.FinanceRoutes })),
)

// Parent-portal module (root parent dashboard) + companion parent-facing
// modules ported in Phase 2. Each was `app/<slug>/*` in EdemicsAllModule and
// now lives as a lazy react-router feature under apps/tenant-app.
const ParentPortalRoutes = lazyWithRetry(() =>
  import("./features/parent-portal/routes").then((m) => ({ default: m.ParentPortalRoutes })),
)
const AdmissionsRoutes = lazyWithRetry(() =>
  import("./features/admissions/routes").then((m) => ({ default: m.AdmissionsRoutes })),
)
const CommunicationRoutes = lazyWithRetry(() =>
  import("./features/communication/routes").then((m) => ({ default: m.CommunicationRoutes })),
)
const ExtracurricularRoutes = lazyWithRetry(() =>
  import("./features/extracurricular/routes").then((m) => ({ default: m.ExtracurricularRoutes })),
)
const ParentMeetingsRoutes = lazyWithRetry(() =>
  import("./features/parent-meetings/routes").then((m) => ({ default: m.ParentMeetingsRoutes })),
)
const SchoolBusRoutes = lazyWithRetry(() =>
  import("./features/school-bus/routes").then((m) => ({ default: m.SchoolBusRoutes })),
)
const SchoolPickupRoutes = lazyWithRetry(() =>
  import("./features/school-pickup/routes").then((m) => ({ default: m.SchoolPickupRoutes })),
)
const SchoolbusRoutes = lazyWithRetry(() =>
  import("./features/schoolbus/routes").then((m) => ({ default: m.SchoolbusRoutes })),
)

// Academics module — biggest single admin surface (175 pages, Phase 3).
const AcademicsRoutes = lazyWithRetry(() =>
  import("./features/academics/routes").then((m) => ({ default: m.AcademicsRoutes })),
)

// IEP — extracted from academics/classroom/iep (2026-09-01) so it can
// be gated behind a premium subscription tier. BE catalog seeds
// `slug: 'iep'` defaultEnabled=false; opting in a tenant unlocks
// this feature.
const IepRoutes = lazyWithRetry(() =>
  import("./features/iep/routes").then((m) => ({ default: m.IepRoutes })),
)

// Student portal — 10 placeholder shells under /student/academics/*, Phase 5.
const StudentPortalRoutes = lazyWithRetry(() =>
  import("./features/student-portal/routes").then((m) => ({ default: m.StudentPortalRoutes })),
)

// HRMS module — employees + performance appraisal (19 pages, Phase 6).
const HrmsRoutes = lazyWithRetry(() =>
  import("./features/hrms/routes").then((m) => ({ default: m.HrmsRoutes })),
)

// Tools module — narrow port covering user-management + tools landing
// (2026-08-27). Other tools (form-builder, workflow, email-template, etc.)
// live on EdemicsAllModule until ported.
const ToolsRoutes = lazyWithRetry(() =>
  import("./features/tools/routes").then((m) => ({ default: m.ToolsRoutes })),
)

// LMS — Learning Management System (course builder, extended learning,
// instructors, lessons, certificates, site report). Ported from
// EdemicsAllModule's app/lms/* tree; backed by src/lms.
const LmsRoutes = lazyWithRetry(() =>
  import("./features/lms/routes").then((m) => ({ default: m.LmsRoutes })),
)

// Wallet Management — 10th platform essential (2026-08-27). Wallets,
// transactions, approvers, payment approvals, withdrawals.
const WalletManagementRoutes = lazyWithRetry(() =>
  import("./features/wallet-management/routes").then((m) => ({ default: m.WalletManagementRoutes })),
)

// Always-on tenant-admin surfaces (not gated by module visibility —
// every tenant admin needs access to their own subscription, company
// setup, settlement account, and platform T&Cs).
const SubscriptionRoutes = lazyWithRetry(() =>
  import("./features/subscription/routes").then((m) => ({ default: m.SubscriptionRoutes })),
)
const CompanySetupRoutes = lazyWithRetry(() =>
  import("./features/company-setup/routes").then((m) => ({ default: m.CompanySetupRoutes })),
)
const PgSettlementRoutes = lazyWithRetry(() =>
  import("./features/pg-settlement/routes").then((m) => ({ default: m.PgSettlementRoutes })),
)
const TermsAndConditionsRoutes = lazyWithRetry(() =>
  import("./features/terms-and-conditions/routes").then((m) => ({ default: m.TermsAndConditionsRoutes })),
)

// Onboarding wizard — post-signup flow. Ported from EdemicsAllModule's
// app/onboarding/ tree (feature/port-onboarding-flow, 2026-08-28). Runs
// AUTHENTICATED but OUTSIDE the AppShell so there's no sidebar/header
// during setup — the wizard's own <OnboardingLayout> is the only chrome.
const OnboardingRoutes = lazyWithRetry(() =>
  import("./features/onboarding/routes").then((m) => ({ default: m.OnboardingRoutes })),
)

/**
 * Root component. Route surface:
 *   /                          → redirect to /dashboard
 *   /* (authenticated shell)   → AppShell (sidebar + header + <Outlet/>)
 *     /dashboard               → DashboardPage
 *     /accounting/*            → AccountingRoutes (Task #105)
 *     /academics/*, ...        → mounted later
 *
 * Every rendered element sits inside:
 *   - <Providers>           — QueryClient, Auth, Permission, Module, Chat
 *   - <ErrorBoundary>       — global fallback if anything throws
 *   - <Suspense>            — Suspense fallback while lazy chunks download
 *
 * AppShell adds its own inner ErrorBoundary so a broken route doesn't take
 * out the sidebar + header. That inner boundary is reset on route change so
 * navigating away from a broken page recovers cleanly.
 */
export function App() {
  return (
    <Providers>
      <RootErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <TenantGuard>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Public auth surface — outside AppShell (no sidebar, no auth guard). */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/otp-verification" element={<OtpVerificationPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Change-password surface. AuthGuard-wrapped so the JWT is
                available to /auth/change-password, but outside AppShell
                so the chrome doesn't render while the user is forced
                through a rotation. The guard itself is what routes here
                when user.mustChangePassword is true — no direct navigation
                is expected. */}
            <Route
              path="/change-password"
              element={
                <AuthGuard>
                  <ChangePasswordPage />
                </AuthGuard>
              }
            />

            {/* Public guest / admission surface — a prospective parent with
                no account can apply, later log in as a guest to check status
                and pay fees. NO AppShell (no sidebar), NO auth guard, NO
                ModuleGate. Do NOT confuse with the authenticated
                `/admissions/*` module below, which is gated. */}
            {/* Federation Phase 4 follow-up — public guest preview.
                Fully unauthenticated; tenant resolves from Host header. */}
            <Route path="/guest/:courseKey" element={<GuestPreviewPage />} />
            <Route path="/admission/apply" element={<AdmissionApplyPage />} />
            <Route path="/admission/pay-fees" element={<AdmissionPayFeesPage />} />
            <Route path="/admission/status" element={<AdmissionStatusPage />} />
            <Route path="/guest-access" element={<GuestAccessPage />} />

            {/* Legacy /onboarding/register + /onboarding/verify-otp from
                EdemicsAllModule redirect to the canonical tenant-app
                public auth pages. Existing signup emails linking to the
                old paths still land the user in the right place. */}
            <Route path="/onboarding/register" element={<Navigate to="/register" replace />} />
            <Route path="/onboarding/verify-otp" element={<Navigate to="/otp-verification" replace />} />

            {/* Onboarding wizard — authenticated, but rendered OUTSIDE the
                AppShell (no sidebar, no header). <AuthGuard> gates the
                whole subtree; each page inside carries its own chrome via
                <OnboardingLayout>. */}
            <Route
              path="/onboarding/*"
              element={
                <AuthGuard>
                  <OnboardingRoutes />
                </AuthGuard>
              }
            />

            {/* Authenticated surface — wrapped by AppShell (sidebar + header + AuthGuard). */}
            {/*
              Module-visibility (2026-08-26 → strict defaults 2026-08-27):
              every module-scoped route is wrapped with <ModuleGate>. When
              the tenant's super-admin has the module disabled, deep-links
              and bookmarks render the <ModuleUnavailablePage> inline — the
              URL stays honest so support can reproduce from a screenshot.
              Keys match SAModule.slug from the catalog seed; moduleName
              is the human-facing label shown on the unavailable page.
              /dashboard stays ungated — it's the safe landing target.
            */}
            <Route element={<AppShell />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              {/* Wave S — tenant Settings → Custom domain. Standalone
                  route under /settings/domains so it doesn't wait on a
                  full Settings module. Guarded by AuthGuard (in
                  AppShell) — BE enforces tenant admin scope. */}
              <Route path="/settings/domains" element={<SettingsDomainsPage />} />

              {/* Federation Phase 1 — publisher-tenant course authoring.
                  BE returns 403 for non-publisher tenants; the sidebar
                  gate hides these entries via disabledForKinds. */}
              <Route path="/courses" element={<CoursesListPage />} />
              <Route path="/courses/:id" element={<CourseEditPage />} />

              {/* Federation Phase 2 — entitlement plane.
                  /publisher/* : publisher-tenant listings surface (BE 403 otherwise)
                  /catalogue    : consumer-tenant catalogue of entitled listings
                  /adoptions    : consumer-tenant adopted courses + overlay editor */}
              <Route path="/publisher/listings" element={<PublisherListingsPage />} />
              <Route path="/publisher/statement" element={<PublisherStatementPage />} />
              <Route path="/publisher/analytics" element={<PublisherAnalyticsPage />} />
              <Route path="/catalogue" element={<CataloguePage />} />
              <Route path="/adoptions" element={<AdoptionsListPage />} />
              <Route path="/adoptions/:id" element={<AdoptionEditPage />} />
              <Route path="/statements" element={<ConsumerStatementPage />} />
              <Route
                path="/accounting/*"
                element={<ModuleGate keys="accounting" moduleName="Accounting"><AccountingRoutes /></ModuleGate>}
              />
              <Route
                path="/finance/*"
                element={<ModuleGate keys="finance" moduleName="Finance"><FinanceRoutes /></ModuleGate>}
              />
              <Route
                path="/parent-portal/*"
                element={<ModuleGate keys="parent-portal" moduleName="Parent Portal"><ParentPortalRoutes /></ModuleGate>}
              />
              <Route
                path="/admissions/*"
                element={<ModuleGate keys="admissions" moduleName="Admissions"><AdmissionsRoutes /></ModuleGate>}
              />
              <Route
                path="/communication/*"
                element={<ModuleGate keys="communication" moduleName="Communication"><CommunicationRoutes /></ModuleGate>}
              />
              <Route
                path="/extracurricular/*"
                element={<ModuleGate keys="extracurricular" moduleName="Extracurricular Clubs"><ExtracurricularRoutes /></ModuleGate>}
              />
              <Route
                path="/parent-meetings/*"
                element={<ModuleGate keys="parent-meetings" moduleName="Parent Meetings"><ParentMeetingsRoutes /></ModuleGate>}
              />
              <Route
                path="/school-bus/*"
                element={<ModuleGate keys="school-bus" moduleName="School Bus"><SchoolBusRoutes /></ModuleGate>}
              />
              <Route
                path="/school-pickup/*"
                element={<ModuleGate keys="school-pickup" moduleName="School Pickup"><SchoolPickupRoutes /></ModuleGate>}
              />
              <Route
                path="/schoolbus/*"
                element={<ModuleGate keys="school-bus" moduleName="School Bus"><SchoolbusRoutes /></ModuleGate>}
              />
              <Route
                path="/academics/*"
                element={<ModuleGate keys="academics" moduleName="Academics"><AcademicsRoutes /></ModuleGate>}
              />
              <Route
                path="/iep/*"
                element={<ModuleGate keys="iep" moduleName="IEP"><IepRoutes /></ModuleGate>}
              />
              <Route
                path="/student/*"
                element={<ModuleGate keys="student-portal" moduleName="Student Portal"><StudentPortalRoutes /></ModuleGate>}
              />
              <Route
                path="/hrms/*"
                element={<ModuleGate keys="hrms" moduleName="HRMS"><HrmsRoutes /></ModuleGate>}
              />
              <Route
                path="/tools/*"
                element={<ModuleGate keys="tools" moduleName="Tools"><ToolsRoutes /></ModuleGate>}
              />
              <Route
                path="/lms/*"
                element={<ModuleGate keys="lms" moduleName="LMS"><LmsRoutes /></ModuleGate>}
              />
              <Route
                path="/wallet-management/*"
                element={<ModuleGate keys="wallet-management" moduleName="Wallet Management"><WalletManagementRoutes /></ModuleGate>}
              />
              {/* Always-on tenant-admin surfaces. No ModuleGate — a
                  tenant with subscription disabled couldn't manage
                  their own subscription to re-enable it (bootstrap
                  trap). Same reasoning for the other three. */}
              <Route path="/subscription/*"          element={<SubscriptionRoutes />} />
              <Route path="/company-setup/*"        element={<CompanySetupRoutes />} />
              <Route path="/pg-settlement/*"        element={<PgSettlementRoutes />} />
              <Route path="/terms-and-conditions/*" element={<TermsAndConditionsRoutes />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          </TenantGuard>
        </Suspense>
      </RootErrorBoundary>
    </Providers>
  )
}

/**
 * Root-level error boundary. Reset key = pathname so navigating away from
 * a broken page automatically re-mounts the tree fresh.
 */
function RootErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[location.pathname]}>
      {children}
    </ErrorBoundary>
  )
}

function NotFound() {
  return (
    <div className="p-8">
      <h1 className="text-2xl font-semibold">Not found</h1>
      <p className="text-muted-foreground mt-1">
        This route hasn't been migrated to tenant-app yet. Check back after Phase 2 completes.
      </p>
    </div>
  )
}
